import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Runs `fetcher` on mount and whenever its identity changes (memoize it with useCallback).
 * Starting a new request aborts the previous one, and a response that arrives after a newer
 * request has started is ignored, so a slow older page can't overwrite a newer one.
 */
export function useAbortableFetch<T>(fetcher: (signal: AbortSignal) => Promise<T>, errorMessage: string) {
    const [data, setData] = useState<T | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Two guards are needed. The AbortController covers the in-flight fetch; the counter also
    // covers the window before it (e.g. `await getToken()` inside the fetcher), where a slower
    // earlier request could still overwrite a newer one's results.
    const abortRef = useRef<AbortController | null>(null);
    const requestIdRef = useRef(0);

    const reload = useCallback(async () => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const requestId = ++requestIdRef.current;

        setLoading(true);
        setError(null);
        try {
            const result = await fetcher(controller.signal);
            if (requestId !== requestIdRef.current) return;
            setData(result);
        } catch (err) {
            if (controller.signal.aborted || requestId !== requestIdRef.current) return;
            console.error(errorMessage, err);
            // Clear the data rather than leaving a stale page behind the error.
            setData(undefined);
            setError(err instanceof Error ? err.message : errorMessage);
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [fetcher, errorMessage]);

    useEffect(() => {
        reload();
        return () => abortRef.current?.abort();
    }, [reload]);

    return { data, loading, error, reload };
}
